import { ActionFunctionArgs, LoaderFunctionArgs, redirect } from "react-router-dom";

import { myAxios } from "src/components/common/functions";

export type NewspaperItem = {
    id: string;
    name: string;
    type: string;
    size: number;
    parentId: string | null;
    description: string;
};

export type NewspaperLoaderData = {
    rootFolderId: string;
    currentFolderId: string;
    title: string;
    currentNewspaperId: string;
    items: NewspaperItem[];
    parents: { id: string; name: string }[];
};

export type ArticleLoaderData = {
    rootFolderId: string;
    folderId: string;
    article: {
        id: string;
        name: string;
        data: string;
    };
    mode: "view" | "edit";
};

export const NEWSPAPER_ROOT_ID = "9c0f2b7a1d4e6f8890ab12cd34ef5678";

export async function newspaperLoader({ params }: LoaderFunctionArgs): Promise<NewspaperLoaderData> {
    const folderId = params.folderId || NEWSPAPER_ROOT_ID;
    const formData = new FormData();
    formData.append("folderId", folderId);
    formData.append("rootFolderId", NEWSPAPER_ROOT_ID);
    const res = await myAxios({
        fileName: "newspaper/readFolder.php",
        formData,
    });
    if (!res) throw redirect("..");
    return {
        rootFolderId: NEWSPAPER_ROOT_ID,
        currentFolderId: folderId,
        title: "Newspaper",
        currentNewspaperId: res.data.currentNewspaperId ?? "",
        items: res.data.items ?? [],
        parents: res.data.parents ?? [],
    };
}

export async function articleLoader({ params, request }: LoaderFunctionArgs): Promise<ArticleLoaderData> {
    const fileId = params.fileId;
    if (!fileId) throw redirect("/Newspaper");
    const url = new URL(request.url);
    const folderId = url.searchParams.get("folderId") || NEWSPAPER_ROOT_ID;
    const formData = new FormData();
    formData.append("fileId", fileId);
    formData.append("rootFolderId", NEWSPAPER_ROOT_ID);
    const res = await myAxios({
        fileName: "newspaper/readArticle.php",
        formData,
    });
    if (!res) throw redirect("..");
    return {
        rootFolderId: NEWSPAPER_ROOT_ID,
        folderId,
        article: res.data,
        mode: params.mode === "Edit" ? "edit" : "view",
    };
}

export async function articleAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => {
        controller.abort();
    });
    const formData = await request.formData();
    const action = String(formData.get("action") ?? "");
    const fileName = action === "Add" ? "newspaper/createArticle.php" : action === "Edit" ? "newspaper/updateArticle.php" : "";
    if (!fileName) {
        window.alert("Invalid newspaper action.");
        return null;
    }
    if (!formData.get("subject")) {
        window.alert("Title is required.");
        return null;
    }
    const res = await myAxios({
        fileName,
        formData,
        signal: controller.signal,
    });
    if (!res) return null;
    const folderId = String(formData.get("parentFolderId") || "");
    return redirect(folderId && folderId !== NEWSPAPER_ROOT_ID ? `/Newspaper/${folderId}` : "/Newspaper");
}

export async function newspaperMutationAction({ request, params }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => {
        controller.abort();
    });
    const kind = params.kind;
    const map = {
        createFolder: "newspaper/createFolder.php",
        rename: "newspaper/renameFile.php",
        delete: "newspaper/deleteFile.php",
        setCurrent: "newspaper/setCurrent.php",
    } as const;
    const fileName = kind ? map[kind as keyof typeof map] : undefined;
    if (!fileName) throw new Response("Invalid action", { status: 400 });
    const formData = await request.formData();
    const res = await myAxios({
        fileName,
        formData,
        signal: controller.signal,
    });
    if (!res) throw redirect("..");
    throw redirect("..");
}

